<template>
  <teleport to="body">
    <v-navigation-drawer v-model="drawerOpen" location="right" temporary width="520">
      <div class="pa-5 d-flex flex-column ga-4">
        <div class="d-flex align-start justify-space-between ga-3">
          <div>
            <div class="text-caption text-medium-emphasis">{{ $t(`${copyScope}.eyebrow`) }}</div>
            <h3 class="text-h6">{{ $t(`${copyScope}.title`) }}</h3>
            <p class="text-body-2 text-medium-emphasis mb-0">{{ $t(`${copyScope}.hint`) }}</p>
          </div>
          <v-btn icon="mdi-close" variant="text" :aria-label="$t('app.actions.close')" @click="drawerOpen = false" />
        </div>
        <v-skeleton-loader v-if="loading" type="paragraph, list-item-two-line@4" />
        <template v-else>
          <v-alert v-if="retryRequired" type="warning">
            {{ $t(`${copyKey}.uncertainSave`) }}
            <template #append>
              <v-btn variant="text" :loading="saving" @click="retrySave">{{ $t(`${copyKey}.retrySameRequest`) }}</v-btn>
            </template>
          </v-alert>
          <v-alert v-else-if="pendingSave && pendingSave.subjectKey !== subjectKey" type="info">
            {{ $t(`${copyKey}.otherSavePending`) }}
          </v-alert>
          <v-alert v-else-if="errorMessage" type="error" closable @click:close="errorMessage = ''">
            {{ errorMessage }}
            <template v-if="optionsFailed" #append>
              <v-btn variant="text" @click="loadOptions">{{ $t(`${copyKey}.retry`) }}</v-btn>
            </template>
          </v-alert>
          <v-alert v-if="!optionsFailed && !isCompanyObjection && !contactPoints.length" type="info">{{ $t(`${copyKey}.noContactPoints`) }}</v-alert>
          <v-alert v-else-if="!purposes.length" type="info">{{ $t(`${copyKey}.noPurposes`) }}</v-alert>
          <template v-else>
            <v-select v-if="!isCompanyObjection" v-model="form.contactPointId" :items="contactPointOptions" item-title="title" item-value="value"
              :label="$t(`${copyKey}.contactPoint`)" :disabled="formLocked" @update:model-value="ensureCompatiblePurpose" />
            <v-select v-model="form.purposeId" :items="availablePurposes" item-title="name" item-value="id"
              :label="$t(`${copyKey}.purpose`)" :disabled="formLocked" @update:model-value="ensureCompatibleChannel" />
            <v-select v-if="isCompanyObjection" v-model="form.channelCode" :items="channelOptions" item-title="title" item-value="value"
              :label="$t(`${copyKey}.channel`)" :hint="$t(`${copyKey}.channelHint`)" persistent-hint
              :disabled="formLocked" />
            <v-select v-if="!isCompanyObjection" v-model="form.eventType" :items="eventTypes" item-title="title" item-value="value"
              :label="$t(`${copyKey}.eventType`)" :disabled="formLocked" />
            <NcrmDateTimeField v-model="form.occurredAt" :label="$t(`${copyKey}.occurredAt`)" :disabled="formLocked" />
            <v-text-field v-model="form.collectedPlace" :label="$t(`${copyKey}.collectedPlace`)" :disabled="formLocked" />
            <v-textarea v-model="form.capturedText" :label="$t(`${copyKey}.capturedText`)"
              rows="4" :counter="10000" maxlength="10000" :disabled="formLocked" />
            <v-select v-model="form.locale" :items="localeOptions" item-title="title" item-value="value"
              :label="$t(`${copyKey}.locale`)" :disabled="formLocked" />
            <v-file-input v-model="form.file" accept="application/pdf,image/jpeg,image/png" clearable
              prepend-icon="mdi-paperclip" :label="$t(`${copyKey}.${attachmentRequired ? 'fileRequired' : 'fileOptional'}`)"
              :hint="$t(`${copyKey}.fileHint`)" persistent-hint :disabled="formLocked" />
            <v-alert type="info">
              {{ previewText }}
            </v-alert>
          </template>
          <div class="d-flex justify-end ga-2">
            <v-btn variant="text" @click="drawerOpen = false">{{ $t("app.actions.cancel") }}</v-btn>
            <v-btn color="primary" :loading="saving" :disabled="!canSubmit" @click="submit">
              {{ $t(`${copyScope}.save`) }}
            </v-btn>
          </div>
        </template>
      </div>
    </v-navigation-drawer>
  </teleport>
</template>

<script>
import axios from "axios";
import NcrmDateTimeField from "@/components/ncrm/shared/NcrmDateTimeField.vue";

const NEGATIVE_EVENTS = new Set(["refused", "withdrawn", "objected"]);
const SAVE_TIMEOUT_MS = 30000;
const localDateTime = () => {
  const date = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
  return date.toISOString().slice(0, 16);
};
const newIdempotencyKey = () => globalThis.crypto?.randomUUID?.()
  || `privacy-paper-${Date.now()}-${Math.random().toString(36).slice(2)}`;
export default {
  name: "NcrmPrivacyEvidenceDrawer",
  components: { NcrmDateTimeField },
  props: {
    modelValue: { type: Boolean, default: false },
    subjectType: { type: String, required: true },
    subjectId: { type: [String, Number], required: true },
    // `company_objection` consemnează o opoziție la nivelul companiei: același
    // serviciu, aceeași idempotență și aceeași dovadă, dar scope `subject`.
    scope: {
      type: String,
      default: "contact_point",
      validator: (value) => ["contact_point", "company_objection"].includes(value),
    },
    // S8.5c: tipul preselectat la deschidere (de ex. `objected` din plângerea spam a unei persoane).
    defaultEventType: { type: String, default: null },
  },
  emits: ["update:modelValue", "saved"],
  data: () => ({
    loading: false, saving: false, optionsFailed: false, errorMessage: "",
    contactPoints: [], purposes: [], attachmentRequired: false, idempotencyKey: "", optionsSequence: 0,
    pendingSave: null,
    form: { contactPointId: null, purposeId: null, channelCode: null, eventType: "granted", occurredAt: "",
      collectedPlace: "", capturedText: "", locale: "ro", file: null },
  }),
  computed: {
    copyKey() { return "ncrm.contacts.dataProtection.record.evidence"; },
    isCompanyObjection() { return this.scope === "company_objection"; },
    // Titlul, explicația și butonul aparțin acțiunii pe care a pornit-o
    // utilizatorul. Cine apasă „Înregistrează opoziție" nu trebuie să
    // primească un drawer intitulat „Înregistrează dovadă".
    copyScope() { return this.isCompanyObjection ? `${this.copyKey}.objection` : this.copyKey; },
    drawerOpen: {
      get() { return this.modelValue; },
      set(value) { this.$emit("update:modelValue", value); },
    },
    subjectKey() { return `${this.subjectType}:${this.subjectId}`; },
    selectedContactPoint() {
      return this.contactPoints.find((item) => Number(item.id) === Number(this.form.contactPointId)) || null;
    },
    availablePurposes() {
      if (this.isCompanyObjection) return this.purposes.filter((item) => item.channels?.length);
      const channel = this.selectedContactPoint?.type;
      return channel ? this.purposes.filter((item) => item.channels?.includes(channel)) : this.purposes;
    },
    selectedPurpose() {
      return this.purposes.find((item) => Number(item.id) === Number(this.form.purposeId)) || null;
    },
    channelOptions() {
      return (this.selectedPurpose?.channels || []).map((value) => ({
        value,
        title: this.$t(`ncrm.contacts.dataProtection.record.channels.${value}`),
      }));
    },
    contactPointOptions() {
      return this.contactPoints.map((item) => ({
        value: item.id,
        title: `${this.$t(`${this.copyKey}.channels.${item.type}`)} · ${item.value_display}`,
      }));
    },
    eventTypes() {
      return ["granted", "refused", "withdrawn", "objected"]
        .map((value) => ({ value, title: this.$t(`${this.copyKey}.events.${value}`) }));
    },
    localeOptions() {
      return ["ro", "en", "ru"].map((value) => ({ value, title: value.toUpperCase() }));
    },
    selectedFile() { return Array.isArray(this.form.file) ? this.form.file[0] : this.form.file; },
    retryRequired() { return Boolean(this.pendingSave?.failed && this.pendingSave.subjectKey === this.subjectKey); },
    formLocked() { return this.saving || Boolean(this.pendingSave); },
    canSubmit() {
      const target = this.isCompanyObjection ? this.form.channelCode : this.selectedContactPoint;
      return !this.formLocked && Boolean(target && this.form.purposeId && this.form.eventType
        && this.form.occurredAt && this.form.collectedPlace.trim() && this.form.capturedText.trim()
        && (!this.attachmentRequired || this.selectedFile));
    },
    previewText() {
      const target = this.isCompanyObjection
        ? (this.form.channelCode
          && this.$t(`ncrm.contacts.dataProtection.record.channels.${this.form.channelCode}`))
        : this.selectedContactPoint?.value_display;
      if (!target || !this.form.purposeId) return this.$t(`${this.copyScope}.previewEmpty`);
      return this.$t(`${this.copyKey}.${this.isCompanyObjection ? "previewCompany" : "preview"}`, {
        event: this.$t(`${this.copyKey}.events.${this.form.eventType}`),
        contact: target,
        purpose: this.selectedPurpose?.name || "—",
      });
    },
  },
  watch: {
    modelValue(value) {
      if (!value) return;
      if (this.pendingSave?.subjectKey === this.subjectKey) { this.restorePendingSave(); return; }
      this.resetForm(); this.loadOptions();
    },
    subjectKey() {
      if (this.modelValue) this.drawerOpen = false;
      if (this.pendingSave?.subjectKey === this.subjectKey) this.restorePendingSave();
      else this.resetForm();
    },
  },
  methods: {
    resetForm() {
      this.errorMessage = ""; this.optionsFailed = false; this.loading = false;
      this.saving = Boolean(this.pendingSave && !this.pendingSave.failed);
      this.contactPoints = []; this.purposes = []; this.attachmentRequired = false;
      this.optionsSequence += 1;
      this.idempotencyKey = newIdempotencyKey();
      this.form = { contactPointId: null, purposeId: null, channelCode: null,
        eventType: this.isCompanyObjection ? "objected" : (this.defaultEventType || "granted"), occurredAt: localDateTime(),
        collectedPlace: "", capturedText: "", locale: "ro", file: null };
    },
    restorePendingSave() {
      const pending = this.pendingSave;
      if (!pending || pending.subjectKey !== this.subjectKey) return;
      this.optionsSequence += 1;
      this.errorMessage = ""; this.optionsFailed = false; this.loading = false;
      this.form = { ...pending.form };
      this.contactPoints = pending.contactPoints;
      this.purposes = pending.purposes;
      this.attachmentRequired = pending.attachmentRequired;
      this.idempotencyKey = pending.idempotencyKey;
      this.saving = !pending.failed;
    },
    async loadOptions() {
      const sequence = ++this.optionsSequence;
      this.loading = true; this.errorMessage = ""; this.optionsFailed = false;
      try {
        const { data } = await axios.get("/ncrm/privacy/evidence/paper/options", {
          params: { subject_type: this.subjectType, subject_id: this.subjectId },
        });
        if (sequence !== this.optionsSequence) return;
        this.contactPoints = Array.isArray(data?.contact_points) ? data.contact_points : [];
        this.purposes = Array.isArray(data?.purposes) ? data.purposes : [];
        this.attachmentRequired = Boolean(data?.attachment_required);
        if (!this.contactPoints.some((item) => Number(item.id) === Number(this.form.contactPointId))) {
          this.form.contactPointId = null;
        }
        this.ensureCompatiblePurpose();
        this.ensureCompatibleChannel();
      } catch {
        if (sequence !== this.optionsSequence) return;
        this.optionsFailed = true;
        this.errorMessage = this.$t(`${this.copyKey}.errors.loadError`);
      } finally { if (sequence === this.optionsSequence) this.loading = false; }
    },
    ensureCompatiblePurpose() {
      if (!this.availablePurposes.some((item) => Number(item.id) === Number(this.form.purposeId))) {
        this.form.purposeId = null;
      }
    },
    ensureCompatibleChannel() {
      if (!this.isCompanyObjection) return;
      if (!this.channelOptions.some(({ value }) => value === this.form.channelCode)) {
        this.form.channelCode = null;
      }
    },
    errorKey(error) {
      const code = error?.response?.data?.code;
      if (["privacy_file_empty", "privacy_file_type_not_allowed", "privacy_file_signature_mismatch",
        "privacy_file_extension_mismatch"].includes(code)) return "privacy_upload_invalid";
      return ["privacy_attachment_required", "privacy_negative_state_override_denied", "privacy_file_too_large",
        "privacy_upload_invalid", "privacy_not_found", "privacy_purpose_inactive"].includes(code) ? code : "submitError";
    },
    isAmbiguousFailure(error) {
      const status = Number(error?.response?.status);
      return !status || status >= 500 || status === 408 || status === 429;
    },
    isCurrentPending(pending) {
      // `data()` is deeply reactive in Vue 3. Once assigned to
      // `this.pendingSave`, a plain object is returned as a Proxy, therefore
      // comparing it by reference with the local `pending` object rejects the
      // response of the very same request. The idempotency key is unique per
      // save and remains unchanged for an intentional retry, so together with
      // the subject it is the stable operation identity we need here.
      return Boolean(
        pending
        && this.pendingSave?.idempotencyKey === pending.idempotencyKey
        && this.pendingSave?.subjectKey === pending.subjectKey
      );
    },
    async submit() {
      if (!this.canSubmit) return;
      const pending = {
        subjectKey: this.subjectKey, idempotencyKey: this.idempotencyKey,
        form: { ...this.form }, contactPoints: this.contactPoints, purposes: this.purposes,
        attachmentRequired: this.attachmentRequired, body: null, failed: false,
      };
      const purpose = this.selectedPurpose;
      // Opoziția companiei este un eveniment subject-scoped: se aplică tuturor
      // punctelor active ale companiei pentru scopul și canalul alese, inclusiv
      // celor adăugate ulterior.
      const target = this.isCompanyObjection
        ? {
          scope_type: "subject", subject_type: "company", ncrm_company_id: Number(this.subjectId),
          channel_code: this.form.channelCode,
        }
        : {
          scope_type: "contact_point", contact_point_id: this.selectedContactPoint.id,
          channel_code: this.selectedContactPoint.type,
        };
      const payload = {
        ...target,
        purpose_id: Number(this.form.purposeId),
        event_type: this.form.eventType, occurred_at: new Date(this.form.occurredAt).toISOString(),
        collected_place: this.form.collectedPlace.trim(), captured_text: this.form.capturedText.trim(),
        locale: this.form.locale, lawful_basis_code: purpose?.default_lawful_basis_code || null,
        ...(NEGATIVE_EVENTS.has(this.form.eventType) ? { reason_code: "requested_by_subject" } : {}),
      };
      const body = new FormData();
      body.append("payload", JSON.stringify(payload));
      if (this.selectedFile) body.append("file", this.selectedFile);
      pending.body = body;
      this.pendingSave = pending;
      await this.sendPendingSave(pending);
    },
    async retrySave() {
      const pending = this.pendingSave;
      if (!pending?.failed || pending.subjectKey !== this.subjectKey || this.saving) return;
      pending.failed = false;
      await this.sendPendingSave(pending);
    },
    async sendPendingSave(pending) {
      this.saving = true; this.errorMessage = "";
      try {
        const { data } = await axios.post("/ncrm/privacy/evidence/paper", pending.body,
          { headers: { "Idempotency-Key": pending.idempotencyKey }, timeout: SAVE_TIMEOUT_MS });
        if (!this.isCurrentPending(pending) || pending.subjectKey !== this.subjectKey) return;
        // Închidem și deblocăm drawerul înainte ca părintele să pornească
        // refresh-ul recordului. Astfel, un handler de succes lent sau defect nu
        // poate lăsa formularul vizibil cu spinnerul activ după ce API-ul a
        // confirmat persistența.
        this.pendingSave = null;
        this.saving = false;
        this.idempotencyKey = newIdempotencyKey();
        this.$emit("update:modelValue", false);
        this.$emit("saved", { data, subjectKey: pending.subjectKey });
      } catch (error) {
        if (!this.isCurrentPending(pending)) return;
        pending.failed = this.isAmbiguousFailure(error);
        if (pending.subjectKey !== this.subjectKey) return;
        this.restorePendingSave();
        if (pending.failed) return;
        const errorKey = this.errorKey(error);
        this.optionsFailed = ["privacy_not_found", "privacy_purpose_inactive"].includes(errorKey);
        this.errorMessage = this.$t(`${this.copyKey}.errors.${errorKey}`);
      } finally {
        if (this.isCurrentPending(pending)) {
          this.saving = false;
          if (!pending.failed) this.pendingSave = null;
        }
      }
    },
  },
};
</script>
